import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { GRN, GRNFormData, GRNStatus } from '@/types/procurement';

interface GRNFilters {
  search?: string;
  status?: GRNStatus | 'all';
  po_number?: string;
  supplier_id?: string;
  date_from?: string;
  date_to?: string;
}

interface GRNKPIs {
  pending: number;
  partial: number;
  received: number;
  rejected: number;
  total: number;
}

export const useGRN = () => {
  const [grns, setGRNs] = useState<GRN[]>([]);
  const [loading, setLoading] = useState(false);
  const [kpis, setKPIs] = useState<GRNKPIs>({
    pending: 0,
    partial: 0,
    received: 0,
    rejected: 0,
    total: 0,
  });

  // Fetch GRNs with related data
  const fetchGRNs = useCallback(async (filters?: GRNFilters) => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('grn')
        .select(`
          id,
          code,
          po_id,
          receipt_date,
          received_by,
          status,
          created_at,
          updated_at,
          purchase_order:purchase_order(code, order_date, currency, supplier:suppliers(id, name, code)),
          receiver:users!fk_grn_received_by_users(id, full_name, email),
          grn_lines:grn_line(
            id,
            item_id,
            qty_received,
            qty_rejected,
            qty_accepted,
            unit_price,
            tax_rate,
            reject_reason,
            qc_required,
            po_line_id,
            po_line:po_line(id, qty_ordered, uom),
            item:inventory_items(id, code, name, unit)
          )
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters?.search) {
        query = query.or(`code.ilike.%${filters.search}%,purchase_order.code.ilike.%${filters.search}%`);
      }

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.supplier_id) {
        query = query.eq('purchase_order.supplier_id', filters.supplier_id);
      }

      if (filters?.date_from) {
        query = query.gte('received_date', filters.date_from);
      }

      if (filters?.date_to) {
        query = query.lte('received_date', filters.date_to);
      }

      const { data, error } = await query;

      if (error) throw error;

      const transformedData: GRN[] = (data || []).map(grn => ({
        ...grn,
        grn_number: grn.code, // Map DB 'code' to frontend 'grn_number'
        received_date: grn.receipt_date, // Map DB 'receipt_date' to frontend 'received_date'
        purchase_order: grn.purchase_order as any,
        receiver: grn.receiver as any,
        grn_lines: grn.grn_lines as any,
      }));

      setGRNs(transformedData);
      calculateKPIs(transformedData);
    } catch (error: any) {
      console.error('Error fetching GRNs:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch GRNs',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Calculate KPIs from GRN data
  const calculateKPIs = (grnData: GRN[]) => {
    const kpis = grnData.reduce(
      (acc, grn) => {
        acc.total++;
        if (grn.status === 'DRAFT' || grn.status === 'PENDING') {
          acc.pending++;
        } else if (grn.status === 'PARTIAL') {
          acc.partial++;
        } else if (grn.status === 'RECEIVED') {
          acc.received++;
        } else if (grn.status === 'CANCELLED') {
          acc.rejected++;
        }
        return acc;
      },
      { pending: 0, partial: 0, received: 0, rejected: 0, total: 0 }
    );

    setKPIs(kpis);
  };

  // Create new GRN
  const createGRN = async (data: GRNFormData): Promise<string | null> => {
    try {
      setLoading(true);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('User not authenticated');

      // Generate GRN number
      const grnNumber = await generateGRNNumber();

      // Create GRN
      const { data: grnData, error: grnError } = await supabase
        .from('grn')
        .insert({
          code: grnNumber,
          po_id: data.po_id,
          receipt_date: data.received_date,
          received_by: user.id,
          status: 'DRAFT',
        })
        .select('id')
        .single();

      if (grnError) throw grnError;

      // Create GRN lines
      if (data.lines && data.lines.length > 0) {
        const { error: linesError } = await supabase
          .from('grn_line')
          .insert(
            data.lines.map(line => ({
              grn_id: grnData.id,
              po_line_id: line.po_line_id,
              item_id: line.item_id,
              qty_received: line.qty_received,
              qty_rejected: line.qty_rejected || 0,
              qty_accepted: (line.qty_received || 0) - (line.qty_rejected || 0),
              uom: line.uom || 'NOS',
              unit_price: line.unit_price,
              tax_rate: line.tax_rate || 0,
              reject_reason: line.reject_reason,
              qc_required: line.qc_required || false,
            }))
          );

        if (linesError) throw linesError;
      }

      toast({
        title: 'Success',
        description: 'GRN created successfully',
      });

      await fetchGRNs();
      return grnData.id;
    } catch (error: any) {
      console.error('Error creating GRN:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create GRN',
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Update GRN
  const updateGRN = async (grnId: string, data: Partial<GRNFormData>): Promise<boolean> => {
    try {
      setLoading(true);

      // Update GRN
      const { error: grnError } = await supabase
        .from('grn')
        .update({
          received_date: data.received_date,
          notes: data.notes,
        })
        .eq('id', grnId);

      if (grnError) throw grnError;

      // Update lines if provided
      if (data.lines) {
        // Delete existing lines
        const { error: deleteError } = await supabase
          .from('grn_line')
          .delete()
          .eq('grn_id', grnId);

        if (deleteError) throw deleteError;

        // Insert updated lines
        const { error: insertError } = await supabase
          .from('grn_line')
          .insert(
            data.lines.map(line => ({
              grn_id: grnId,
              item_id: line.item_id,
              qty_received: line.qty_received,
              qty_rejected: line.qty_rejected || 0,
              qty_accepted: (line.qty_received || 0) - (line.qty_rejected || 0),
              unit_price: line.unit_price,
              tax_rate: line.tax_rate || 0,
              reject_reason: line.reject_reason,
              qc_required: line.qc_required || false,
            }))
          );

        if (insertError) throw insertError;
      }

      toast({
        title: 'Success',
        description: 'GRN updated successfully',
      });

      await fetchGRNs();
      return true;
    } catch (error: any) {
      console.error('Error updating GRN:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update GRN',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Process GRN (mark as received and trigger inventory posting)
  const processGRN = async (grnId: string): Promise<boolean> => {
    try {
      setLoading(true);

      // Call edge function to process GRN and post to inventory
      const { error } = await supabase.functions.invoke('grn-post-inventory', {
        body: { grn_id: grnId },
      });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'GRN processed and inventory updated successfully',
      });

      await fetchGRNs();
      return true;
    } catch (error: any) {
      console.error('Error processing GRN:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to process GRN',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete GRN
  const deleteGRN = async (grnId: string): Promise<boolean> => {
    try {
      setLoading(true);

      // Check if GRN can be deleted (only PENDING status)
      const { data: grnData, error: checkError } = await supabase
        .from('grn')
        .select('status')
        .eq('id', grnId)
        .single();

      if (checkError) throw checkError;

      if (grnData.status !== 'PENDING' && grnData.status !== 'DRAFT') {
        throw new Error('Only draft or pending GRNs can be deleted');
      }

      // Delete GRN (cascade will handle lines)
      const { error: deleteError } = await supabase
        .from('grn')
        .delete()
        .eq('id', grnId);

      if (deleteError) throw deleteError;

      toast({
        title: 'Success',
        description: 'GRN deleted successfully',
      });

      await fetchGRNs();
      return true;
    } catch (error: any) {
      console.error('Error deleting GRN:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete GRN',
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get single GRN by ID
  const getGRN = async (grnId: string): Promise<GRN | null> => {
    try {
      const { data, error } = await supabase
        .from('grn')
        .select(`
          id,
          code,
          po_id,
          receipt_date,
          received_by,
          status,
          created_at,
          updated_at,
          purchase_order:purchase_order(
            id,
            code,
            order_date,
            currency,
            supplier:suppliers(
              id,
              name,
              code
            )
          ),
          receiver:users!fk_grn_received_by_users(
            id,
            full_name,
            email
          ),
          grn_lines:grn_line(
            id,
            item_id,
            qty_received,
            qty_rejected,
            qty_accepted,
            unit_price,
            tax_rate,
            reject_reason,
            qc_required,
            po_line:po_line(
              id,
              qty_ordered,
              uom
            ),
            item:inventory_items(
              id,
              code,
              name,
              unit
            )
          )
        `)
        .eq('id', grnId)
        .single();

      if (error) throw error;

      return {
        ...data,
        grn_number: data.code,
        received_date: data.receipt_date,
        purchase_order: data.purchase_order as any,
        receiver: data.receiver as any,
        grn_lines: data.grn_lines as any,
      } as GRN;
    } catch (error: any) {
      console.error('Error fetching GRN:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch GRN details',
        variant: 'destructive',
      });
      return null;
    }
  };

  // Generate unique GRN number
  const generateGRNNumber = async (): Promise<string> => {
    const prefix = 'GRN';
    const year = new Date().getFullYear().toString().slice(-2);
    const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
    
    // Get the latest GRN number for current month
    const { data, error } = await supabase
      .from('grn')
      .select('code')
      .like('code', `${prefix}${year}${month}%`)
      .order('code', { ascending: false })
      .limit(1);

    if (error) throw error;

    let sequence = 1;
    if (data && data.length > 0) {
      const lastNumber = data[0].code;
      const lastSequence = parseInt(lastNumber.slice(-4));
      sequence = lastSequence + 1;
    }

    return `${prefix}${year}${month}${sequence.toString().padStart(4, '0')}`;
  };

  // Initialize data on mount
  useEffect(() => {
    fetchGRNs();
  }, []);

  return {
    grns,
    loading,
    kpis,
    fetchGRNs,
    createGRN,
    updateGRN,
    processGRN,
    deleteGRN,
    getGRN,
  };
};
